import React, { ReactElement, useEffect, useState } from "react";

import "../styles.scss";
import "../scripts";

import {
  KameleonElement,
  primaryCollectionBlock,
  getDefaultProps,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";
import { WebflowComponentProps, webflowHOC } from "@kameleon-common/webflow-js";
import {
  BackgroundColors as ThemeBackgroundColors,
  Variants,
} from "@kameleon-util/global-theme";
import { v4 as uuidv4 } from "uuid";
import {
  blockConfig,
  ContentCollectionItem,
  ContentCollectionItemType,
} from "./components/content-collection-item";

import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import {
  ContentCollectionType as ComponentType,
  layoutOptions,
} from "./types/prop-types";
import getEvents from "./js/webflow";

type ContentCollectionType = {
  PrimaryBlocks: ContentCollectionItemType[];
} & ComponentType;

type PassThroughProps = {
  mediumMediaId: string;
};
type CollectionWebflowProps = WebflowComponentProps<
  ContentCollectionType,
  PassThroughProps
>;

const Component = ({
  PrimaryBlocks,
  tagline,
  header,
  description,
  passThrough,
}: CollectionWebflowProps): ReactElement => {
  const [mediumMediaId] = useState<string>(uuidv4());

  useEffect(() => {
    passThrough?.({ mediumMediaId });
  }, []);

  return (
    <Box className="primary-collection-content-collection-three-col-medium-media-v1">
      <Box className="padding-global">
        <Box className="container-large">
          <Box className="padding-section-large">
            <Box className="margin-bottom margin-xxlarge">
              <Box className="text-align-center">
                <Box className="max-width-large align-center">
                  {tagline && (
                    <Box className="margin-bottom margin-xsmall tagline">
                      <Variants.TagLine>{tagline}</Variants.TagLine>
                    </Box>
                  )}
                  <Box className="margin-bottom margin-small">
                    <Variants.Heading>{header}</Variants.Heading>
                  </Box>
                  <Variants.ParagraphMedium>
                    {description}
                  </Variants.ParagraphMedium>
                </Box>
              </Box>
            </Box>
            <Box className="layout423_component">
              <Box className="layout423_row">
                {PrimaryBlocks?.map((primaryBlockProps, index) => {
                  const primaryProps = { ...primaryBlockProps };
                  primaryProps.layoutProps = {
                    ...(primaryProps.layoutProps ?? {}),
                    backgroundColor: ThemeBackgroundColors.dark,
                  };
                  return (
                    <Variants.Card
                      key={index}
                      data-w-id={mediumMediaId}
                      className="layout423_card"
                    >
                      <ContentCollectionItem {...primaryProps} />
                    </Variants.Card>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ComponentWithWebflow = webflowHOC<
  CollectionWebflowProps,
  PassThroughProps
>(Component, [
  {
    module: "ix2",
    getEvents: (pageId, passThrough): object =>
      getEvents(pageId, passThrough?.mediumMediaId),
  },
]);

export const ContentCollection = primaryCollectionBlock<ContentCollectionType>(
  blockConfig,
  ({ PrimaryBlocks, ...props }: ContentCollectionType) => (
    <ComponentWithWebflow PrimaryBlocks={PrimaryBlocks} {...props} />
  )
);

ContentCollection.defaultProps =
  getDefaultProps<ContentCollectionType>(defaults);

export const Element: KameleonElement = {
  component: ContentCollection,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
